import {
  useState,
  useEffect,
  createContext,
  useContext,
  Fragment,
} from "react";
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.jsx";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";
import { Switch } from "@/components/ui/switch.jsx";
import {
  Edit2,
  Lock,
  Trash2,
  ArrowLeft,
  X,
  Check,
  Users,
  Loader2,
  Mail,
  Phone,
  Building2,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/context/AuthContext.jsx";
import { useToast } from "@/hooks/use-toast.js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { api } from "@/lib/api/api";
import authAPI from "@/lib/api/auth";
import { formatISO } from "date-fns";

const SettingsContext = createContext(null);

function SettingsProvider({ children }) {
  const [isEditing, setIsEditing] = useState(null);
  const [phoneEditMode, setPhoneEditMode] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);

  // Password update states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isLoadingPassword, setIsLoadingPassword] = useState(false);

  // OTP verification states
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");

  // Users list states
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);

  // Invite modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);

  // Company data states
  const [companyData, setCompanyData] = useState(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [companyError, setCompanyError] = useState(null);

  // SIREN edit states
  const [sirenEditMode, setSirenEditMode] = useState(false);
  const [sirenValue, setSirenValue] = useState("");

  const { user, isLoading, refreshUser } = useAuth();
  const { toast } = useToast();

  // Handle account switching
  const handleAccountSwitch = async (accountId) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8000/api"}/users/${accountId}/switch-account/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            errorData.detail ||
            "Erreur lors du changement de compte",
        );
      }

      toast({
        title: "Compte changé",
        description:
          "Le compte actif a été changé avec succès. Actualisation de la page...",
      });

      // Hard reload the page to clear cached data after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Account switch failed:", error);
      toast({
        title: "Erreur",
        description:
          error.message ||
          "Une erreur est survenue lors du changement de compte",
        variant: "destructive",
      });
    }
  };

  // Get first and last name directly from user object
  const firstName = user?.first_name || "";
  const lastName = user?.last_name || "";

  // Initialize values when user data changes
  useEffect(() => {
    if (user) {
      setPhoneValue(user.phone || "");
      setEmailValue(user.email || "");
    }
  }, [user]);

  // Fetch users list
  const fetchUsers = async () => {
    let allUsers = [];
    setIsLoadingUsers(true);
    setUsersError(null);

    const companyRes = await api.getUserCompany();
    if (!companyRes.ok) {
      setUsersError("Erreur lors du chargement des utilisateurs");
      setIsLoadingUsers(false);
      return;
    }

    const userCompany = await companyRes.json();

    if (userCompany && userCompany.managers) {
      allUsers.push(
        ...userCompany.managers.map((manager) => ({
          id: manager.email,
          // name: manager.full_name,
          email: manager.email,
          status: "active",
        })),
      );
    }

    const invitationsRes = await api.getInvitations();
    if (!invitationsRes.ok) {
      setUsersError("Erreur lors du chargement des utilisateurs");
      setIsLoadingUsers(false);
      return;
    }

    const invitations = await invitationsRes.json();

    if (invitations) {
      allUsers.push(
        ...invitations.map((invitation) => ({
          id: invitation.email,
          // name: "TBD",
          email: invitation.email,
          status: "invited",
          invitation: {
            date: invitation.sent,
            key: invitation.key,
          },
        })),
      );
    }

    setUsers(allUsers);
    setIsLoadingUsers(false);
  };

  const deleteInvitation = async (key) => {
    const response = await api.deleteInvitation(key);

    if (!response.ok) {
      toast({
        title: "Erreur",
        description:
          "Une erreur est survenue lors de la suppression de l'invitation",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Succès",
      description: "L'invitation a bien été supprimée",
    });
    await fetchUsers();
  };

  // Fetch company data for active account
  const fetchCompanyData = async () => {
    setIsLoadingCompany(true);
    setCompanyError(null);

    const response = await api.getUserCompany();
    if (!response.ok) {
      setCompanyError(
        error.message ||
          "Erreur lors du chargement des données de l'entreprise",
      );
      setIsLoadingCompany(false);
      return;
    }

    const userCompany = await response.json();
    setCompanyData(userCompany);
    setIsLoadingCompany(false);
  };

  // Load company data on component mount
  useEffect(() => {
    fetchCompanyData();
    fetchUsers();
  }, []);

  // API functions for updates
  const updatePassword = async () => {
    setIsLoadingPassword(true);
    const response = await authAPI.changePassword(currentPassword, newPassword);
    if (!response.ok) {
      toast({
        title: "Erreur",
        description:
          "Une erreur est survenue lors de la mise à jour du mot de passe",
        variant: "destructive",
      });
      setCurrentPassword("");
      setNewPassword("");
      setIsLoadingPassword(false);
      return;
    }

    toast({
      title: "Mot de passe mis à jour",
      description: "Votre mot de passe a été modifié avec succès.",
    });
    setCurrentPassword("");
    setNewPassword("");
    setIsLoadingPassword(false);
  };

  const updatePhone = async () => {
    setIsLoadingUpdate(true);
    const response = await api.editUser({ phone: phoneValue });
    if (!response.ok) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour",
        variant: "destructive",
      });
      setPhoneEditMode(false);
      setPhoneValue("");
      setIsLoadingUpdate(false);
      return;
    }

    toast({
      title: "Téléphone mis à jour",
      description: "Votre numéro de téléphone a été modifié avec succès.",
    });

    setPhoneEditMode(false);
    setPhoneValue("");
    await refreshUser();
  };

  const requestEmailChange = async () => {
    setIsLoadingUpdate(true);
    const response = await authAPI.changeEMailAddress(emailValue);
    if (!response.ok) {
      toast({
        title: "Erreur",
        description: "Erreur lors de l'envoi du lien de vérification.",
      });
      setEmailEditMode(false);
      setEmailValue("");
      setIsLoadingUpdate(false);
    }
    toast({
      title: "Email de vérification envoyé",
      description:
        "Un lien de vérification a été envoyé à votre nouvelle adresse email.",
    });
    setEmailEditMode(false);
    setEmailValue("");
    setIsLoadingUpdate(false);
  };

  // Handle member invitation
  const handleInviteMember = async () => {
    setIsLoadingInvite(true);
    const response = await api.createInvitation({ email: inviteEmail });
    if (!response.ok) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi de l'invitation",
        variant: "destructive",
      });
      setIsLoadingInvite(false);
      return;
    }

    // Reset modal state
    setInviteEmail("");
    setShowInviteModal(false);
    await fetchUsers();
    setIsLoadingInvite(false);
  };

  const value = {
    user,
    isEditing,
    setIsEditing,
    phoneEditMode,
    setPhoneEditMode,
    emailEditMode,
    setEmailEditMode,
    isLoading,
    isLoadingUpdate,
    isLoadingPassword,
    showOtpModal,
    setShowOtpModal,
    isLoadingOtp,
    users,
    isLoadingUsers,
    usersError,
    showInviteModal,
    setShowInviteModal,
    isLoadingInvite,
    companyData,
    isLoadingCompany,
    companyError,
    sirenEditMode,
    setSirenEditMode,
    sirenValue,
    setSirenValue,
    handleAccountSwitch,
    handleInviteMember,
    updatePassword,
    updatePhone,
    firstName,
    lastName,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    emailValue,
    setEmailValue,
    phoneValue,
    setPhoneValue,
    fetchCompanyData,
    requestEmailChange,
    inviteEmail,
    setInviteEmail,
    deleteInvitation,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

function useSettings() {
  return useContext(SettingsContext);
}

export default function Settings() {
  return (
    <SettingsProvider>
      <SettingsComponent />
    </SettingsProvider>
  );
}

function SettingsComponent() {
  const { user, isLoading } = useSettings();

  // Show loading state while user data is being fetched
  if (isLoading) return <SettingsLoading />;

  // Show error state if no user data is available
  if (!user) return <SettingsNoUser />;

  return (
    <div className="container max-w-4xl mx-auto p-6 space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Paramétrage et données</h1>
        <p className="text-muted-foreground">
          Toutes les informations sur votre compte Okiros
        </p>
      </div>

      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="personal">Données personnelles</TabsTrigger>
          <TabsTrigger value="password">Mot de passe</TabsTrigger>
          {/* <TabsTrigger value="company">Données de l'entreprise</TabsTrigger> */}
          <TabsTrigger value="members">Membres</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="space-y-6">
          <SettingsPersonal />
        </TabsContent>

        <TabsContent value="password" className="space-y-6">
          <SettingsPassword />
        </TabsContent>

        <TabsContent value="company" className="space-y-6">
          <SettingsCompany />
        </TabsContent>

        <TabsContent value="members" className="space-y-6">
          <SettingsMembers />
        </TabsContent>
      </Tabs>

      {/* <OTPDialog /> */}

      <InviteDialg />
    </div>
  );
}

function InviteDialg() {
  const {
    showInviteModal,
    setShowInviteModal,
    isLoadingInvite,
    handleInviteMember,
    inviteEmail,
    setInviteEmail,
  } = useSettings();

  return (
    <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Inviter un membre
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Envoyez une invitation à un nouveau membre pour rejoindre votre
            équipe.
          </p>

          <div className="space-y-3">
            <Label htmlFor="invite-email">Adresse email</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="email@exemple.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isLoadingInvite) {
                  handleInviteMember();
                }
              }}
              disabled={isLoadingInvite}
              autoFocus
            />
          </div>

          <div className="flex space-x-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setShowInviteModal(false);
                setInviteEmail("");
              }}
              disabled={isLoadingInvite}
            >
              Annuler
            </Button>
            <Button
              className="flex-1"
              onClick={handleInviteMember}
              disabled={isLoadingInvite || !inviteEmail.trim()}
            >
              {isLoadingInvite ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer l'invitation
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsPersonal() {
  const {
    user,
    phoneEditMode,
    setPhoneEditMode,
    emailEditMode,
    setEmailEditMode,
    isLoadingUpdate,
    updatePhone,
    firstName,
    lastName,
    emailValue,
    setEmailValue,
    phoneValue,
    setPhoneValue,
    requestEmailChange,
  } = useSettings();

  return (
    <Card>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Prénom</Label>
              <Lock className="h-4 w-4 text-muted-foreground" />
            </div>
            <Input value={firstName || "Non renseigné"} disabled />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Nom</Label>
              <Lock className="h-4 w-4 text-muted-foreground" />
            </div>
            <Input value={lastName || "Non renseigné"} disabled />
          </div>
        </div>

        {/* <div className="space-y-2">
          <Label>Langue</Label>
          <div className="flex space-x-2">
            <Button
              variant={user.language === "en" ? "default" : "outline"}
              size="sm"
            >
              EN
            </Button>
            <Button
              variant={user.language === "fr" ? "default" : "outline"}
              size="sm"
            >
              FR
            </Button>
            <Button
              variant={user.language === "it" ? "default" : "outline"}
              size="sm"
            >
              IT
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Langue actuelle: {user.language?.toUpperCase() || "EN"}
          </p>
        </div> */}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Numéro de téléphone</Label>
            {!phoneEditMode ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPhoneValue(user.phone || "");
                  setPhoneEditMode(true);
                }}
              >
                <Edit2 className="h-4 w-4" />
                Modifier
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPhoneEditMode(false);
                    setPhoneValue("");
                  }}
                >
                  <X className="h-4 w-4" />
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={updatePhone}
                  disabled={isLoadingUpdate}
                >
                  <Check className="h-4 w-4" />
                  Valider
                </Button>
              </div>
            )}
          </div>
          {phoneEditMode ? (
            <Input
              value={phoneValue}
              onChange={(e) => setPhoneValue(e.target.value)}
              placeholder="Entrez votre numéro de téléphone"
            />
          ) : (
            <Input value={user.phone || "Non renseigné"} disabled />
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Adresse courriel</Label>
            {!emailEditMode ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEmailValue(user.email || "");
                  setEmailEditMode(true);
                }}
              >
                <Edit2 className="h-4 w-4" />
                Modifier
              </Button>
            ) : (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEmailEditMode(false);
                    setEmailValue("");
                  }}
                >
                  <X className="h-4 w-4" />
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={requestEmailChange}
                  disabled={isLoadingUpdate}
                >
                  <Check className="h-4 w-4" />
                  Valider
                </Button>
              </div>
            )}
          </div>
          {emailEditMode ? (
            <Input
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              placeholder="Entrez votre nouvelle adresse email"
              type="email"
            />
          ) : (
            <Input value={user.email || "Non renseigné"} disabled />
          )}
        </div>

        {/* <div className="pt-4 border-t">
          <Button variant="destructive" className="w-full">
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer votre compte
          </Button>
        </div> */}
      </CardContent>
    </Card>
  );
}

function SettingsPassword() {
  const {
    isLoadingPassword,
    updatePassword,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
  } = useSettings();

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="current-password">Mot de passe actuel</Label>
          <Input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Entrez votre mot de passe actuel"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password">Nouveau mot de passe</Label>
          <Input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Entrez votre nouveau mot de passe"
          />
        </div>
        <Button
          className="w-full"
          onClick={updatePassword}
          disabled={isLoadingPassword || !currentPassword || !newPassword}
        >
          {isLoadingPassword ? "Mise à jour..." : "Modifier le mot de passe"}
        </Button>
      </CardContent>
    </Card>
  );
}

function SettingsCompany() {
  const {
    user,
    isLoadingUpdate,
    companyData,
    isLoadingCompany,
    companyError,
    sirenEditMode,
    setSirenEditMode,
    sirenValue,
    setSirenValue,
    handleAccountSwitch,
    fetchCompanyData,
  } = useSettings();

  return (
    <Fragment>
      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Account Selection */}
          {/* <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Compte actif
            </Label>
            {user?.accounts && user.accounts.length > 1 ? (
              <Select
                value={
                  user.accounts.find((acc) => acc.active)?.id?.toString() || ""
                }
                onValueChange={(value) => {
                  handleAccountSwitch(value);
                }}
              >
                <SelectTrigger>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <SelectValue placeholder="Sélectionner un compte" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {user.accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id.toString()}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {account.name}
                        {account.active && (
                          <span className="text-xs text-green-600 font-medium">
                            (Actif)
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : user?.accounts && user.accounts.length === 1 ? (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{user.accounts[0].name}</span>
                <span className="text-xs text-green-600 font-medium">
                  (Actif)
                </span>
              </div>
            ) : (
              <Input value="Aucun compte disponible" disabled />
            )}
          </div> */}

          {/* Company Information - From API */}
          {isLoadingCompany ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Chargement des données de l'entreprise...
                </p>
              </div>
            </div>
          ) : companyError ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{companyError}</p>
              <Button onClick={fetchCompanyData} variant="outline">
                Réessayer
              </Button>
            </div>
          ) : companyData ? (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Nom de l'entreprise</Label>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  value={
                    companyData.company_name ||
                    companyData.name ||
                    "Non renseigné"
                  }
                  disabled
                />
              </div>
              {/* 
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Numéro SIREN</Label>
                  {!sirenEditMode ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSirenValue(companyData.identifier || "");
                        setSirenEditMode(true);
                      }}
                    >
                      <Edit2 className="h-4 w-4" />
                      Modifier
                    </Button>
                  ) : (
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSirenEditMode(false);
                          setSirenValue("");
                        }}
                      >
                        <X className="h-4 w-4" />
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          // TODO: Implement SIREN update
                          toast({
                            title: "Fonctionnalité à venir",
                            description:
                              "La mise à jour du SIREN sera disponible prochainement.",
                          });
                        }}
                        disabled={isLoadingUpdate}
                      >
                        <Check className="h-4 w-4" />
                        Valider
                      </Button>
                    </div>
                  )}
                </div>
                {sirenEditMode ? (
                  <Input
                    value={sirenValue}
                    onChange={(e) => setSirenValue(e.target.value)}
                    placeholder="Entrez le numéro SIREN"
                    maxLength={9}
                  />
                ) : (
                  <Input
                    value={companyData.identifier || "Non disponible"}
                    disabled
                  />
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>SIRET</Label>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  value={
                    companyData.siret_number ||
                    companyData.siret ||
                    "Non disponible"
                  }
                  disabled
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Code APE</Label>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  value={companyData.activity_code || "Non disponible"}
                  disabled
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Date d'immatriculation</Label>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  value={
                    companyData.immatriculation_date
                      ? new Date(
                          companyData.immatriculation_date,
                        ).toLocaleDateString("fr-FR")
                      : "Non disponible"
                  }
                  disabled
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Secteur d'activité</Label>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  value={companyData.activities_sector || "Non disponible"}
                  disabled
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Adresse</Label>
                  <Lock className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="space-y-1">
                  <Input
                    value={companyData.address || "Non disponible"}
                    disabled
                  />
                  <Input
                    value={
                      `${companyData.zip_code || ""} ${companyData.town || ""}`.trim() ||
                      "Non disponible"
                    }
                    disabled
                  />
                  <Input
                    value={companyData.country || "Non disponible"}
                    disabled
                  />
                </div>
              </div> */}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                Aucune donnée d'entreprise disponible
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-right space-y-4">
        <div className="bg-muted/50 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">
            Comment utiliser ces informations ?
          </h3>
          <p className="text-sm text-muted-foreground">
            Pour pouvoir vous proposer une analyse personnalisée, nous devons
            pouvoir identifier votre entreprise et la connecter avec nos
            partenaires. Ces informations nous permettent de connaitre et
            l'analyser. Votre déclaration de revenus sera automatiquement
            préparée et votre capacité d'emprunt acceptée.
          </p>
        </div>
      </div>
    </Fragment>
  );
}

function SettingsMembers() {
  const {
    users,
    isLoadingUsers,
    usersError,
    fetchUsers,
    setShowInviteModal,
    deleteInvitation,
  } = useSettings();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Liste des membres
          </CardTitle>
          <Button onClick={() => setShowInviteModal(true)}>Inviter</Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoadingUsers ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Chargement des membres...
              </p>
            </div>
          </div>
        ) : usersError ? (
          <div className="text-center py-8">
            <p className="text-destructive mb-4">{usersError}</p>
            <Button onClick={fetchUsers} variant="outline">
              Réessayer
            </Button>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Aucun membre trouvé</p>
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Statut</TableHead>
                  {users.some(user=>user.invitation) && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{user.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.status === "invited"
                        ? [
                            user.status,
                            formatISO(user.invitation.date, {
                              representation: "date",
                            }),
                          ].join(" ")
                        : user.status}
                    </TableCell>
                    <TableCell className="text-right">
                      {user.invitation && (
                        <Button
                          onClick={() => deleteInvitation(user.invitation.key)}
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SettingsLoading() {
  return (
    <div className="container max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement des paramètres...</p>
        </div>
      </div>
    </div>
  );
}

function SettingsNoUser() {
  const navigate = useNavigate();
  return (
    <div className="container max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">
            Impossible de charger les données utilisateur
          </p>
          <Button onClick={() => navigate("/login")}>Se connecter</Button>
        </div>
      </div>
    </div>
  );
}
